import { QR_ACCEPT_WINDOW_MS, qrTableLabel, summaryOf } from "@/lib/qrAccept";
import type { QRItemSummaryLine, QROrderPendingEventMeta, QRPendingOrder } from "@/types/qr-orders";

/**
 * One Accept prompt.
 * - open: counting down (at 0 it reads "Auto-accepting…" until the order resolves)
 * - accepting: this device's Accept is on its way
 * - attention: the auto-accept could not add it; staff can still accept
 * - resolved: someone (or the auto-accept) resolved it; shows who, then leaves
 */
export interface AcceptPrompt {
  id: string;
  tableId: string | null;
  tableNumber: string | null;
  orderId: string | null;
  lines: QRItemSummaryLine[];
  more: number;
  /** Server clock (ms) when the order is auto-accepted. */
  deadlineAt: number;
  /** The whole accept window (ms), for the progress bar. */
  windowMs: number;
  /** Device clock (ms) when the prompt appeared. */
  shownAt: number;
  phase: "open" | "accepting" | "attention" | "resolved";
  /** "Accepted by Ravi", "Auto-accepted", ... while resolved. */
  outcome?: string;
  /** Why the last Accept failed (the prompt stays). */
  error?: string | null;
  /** Why the auto-accept could not add the order (attention). */
  reason?: string | null;
  /** Past its deadline long enough that the list was asked again. */
  rechecked?: boolean;
  /** Device clock (ms) when this device's Accept went out (while accepting). */
  acceptingSince?: number;
  /** The phase to go back to if the Accept fails or gets no answer. */
  resumePhase?: "open" | "attention";
  /** When the guest placed it (server clock, ms), if known. */
  submittedAt?: number | null;
  /**
   * Device clock (ms) of an Accept from here that got no answer (network error,
   * or given up on): it may have gone through, maybe without its KOT printing.
   */
  unconfirmedAt?: number;
}

export type PromptAction =
  | { type: "add"; prompt: AcceptPrompt }
  | { type: "update"; id: string; patch: Partial<AcceptPrompt> }
  | { type: "remove"; id: string };

/** Prompts in deadline order: the most urgent first, the newest last. */
export function promptsReducer(state: AcceptPrompt[], action: PromptAction): AcceptPrompt[] {
  switch (action.type) {
    case "add":
      if (state.some((p) => p.id === action.prompt.id)) return state;
      return [...state, action.prompt].sort((a, b) => a.deadlineAt - b.deadlineAt);
    case "update":
      return state.map((p) => (p.id === action.id ? { ...p, ...action.patch } : p));
    case "remove":
      return state.filter((p) => p.id !== action.id);
    default:
      return state;
  }
}

const parse = (iso: string | null | undefined): number | null => {
  if (!iso) return null;
  const t = Date.parse(iso);
  return Number.isFinite(t) ? t : null;
};

/** A prompt from the `qr_order_pending` event, or null without a deadline to count to. */
export function promptFromEvent(
  meta: QROrderPendingEventMeta,
  eventCreatedAt: string | null | undefined,
  shownAt: number,
): AcceptPrompt | null {
  if (!meta?.pending_order_id) return null;
  const submitted = parse(meta.submitted_at) ?? parse(eventCreatedAt);
  const deadline = parse(meta.accept_deadline) ?? (submitted !== null ? submitted + QR_ACCEPT_WINDOW_MS : null);
  if (deadline === null) return null;
  const windowMs = submitted !== null && deadline > submitted ? deadline - submitted : QR_ACCEPT_WINDOW_MS;
  return {
    id: meta.pending_order_id,
    tableId: meta.table_id ?? null,
    tableNumber: meta.table_number ?? null,
    orderId: meta.order_id ?? null,
    lines: meta.item_summary ?? [],
    more: meta.more_count ?? 0,
    deadlineAt: deadline,
    windowMs,
    shownAt,
    phase: "open",
  };
}

/** A prompt from a pending-list row, or null for rows without an accept deadline (older servers). */
export function promptFromOrder(order: QRPendingOrder, id: string, shownAt: number): AcceptPrompt | null {
  const deadline = parse(order.accept_deadline);
  if (deadline === null) return null;
  const submitted = parse(order.submitted_at);
  const windowMs = submitted !== null && deadline > submitted ? deadline - submitted : QR_ACCEPT_WINDOW_MS;
  const { lines, more } = summaryOf(order);
  return {
    id,
    tableId: order.table_id ?? null,
    tableNumber: order.table_number ?? null,
    orderId: order.order_id ?? null,
    lines,
    more,
    deadlineAt: deadline,
    windowMs,
    shownAt,
    phase: order.auto_accept_error ? "attention" : "open",
    reason: order.auto_accept_error ?? null,
    submittedAt: submitted,
  };
}

/** "Table 7", or "QR order" when the server left the table out. */
export const tableLabel = (p: Pick<AcceptPrompt, "tableNumber">) => qrTableLabel(p.tableNumber);

/** How long a resolved prompt says who resolved it before it leaves. */
export const RESOLVED_LINGER_MS = 2500;
/** Still "Auto-accepting…" this long after the deadline: ask the server again. */
export const RECHECK_AFTER_MS = 12_000;
/** Open this long past the deadline with no answer at all: let it go. */
export const GIVE_UP_AFTER_MS = 60_000;
/** An Accept with no answer this long: stop waiting, say so, and ask for the list again. */
export const ACCEPT_GIVE_UP_MS = 20_000;
/** A "needs attention" order older than this (since the guest placed it) isn't brought back from the list. */
export const ATTENTION_MAX_AGE_MS = 30 * 60_000;
export const TICK_MS = 250;

/** Guest and sign-in pages: no prompts there, even for a signed-in staff member. */
const PUBLIC_PATHS = [/^\/$/, /^\/order\//, /^\/queue\//, /^\/loyalty\//, /^\/reset-password/, /^\/privacy/, /^\/terms/, /^\/fees-and-refunds/, /^\/select-context/, /^\/brand(\/|$)/];

export function isPromptScreen(pathname: string): boolean {
  if (PUBLIC_PATHS.some((re) => re.test(pathname))) return false;
  // /feedback/:orderId is the guest form unless staff are signed in (checked by the caller).
  return true;
}
